/**
 * Admin · "Maya · Enlace público" for the Dutch Cozumel guide.
 * Generate, copy, open, share (WhatsApp), see usage and revoke links.
 */
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, ExternalLink, Loader2, Plus, Sparkles, Ban, RotateCcw } from "lucide-react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  createCozumelGuideLink,
  listCozumelGuideLinks,
  setCozumelGuideLinkRevoked,
} from "@/lib/cozumel-nl-guide.functions";

export const Route = createFileRoute("/_authenticated/admin/cozumel-nl-links")({
  head: () => ({
    meta: [
      { title: "Admin · Maya · Enlace público (Cozumel NL) — Mostrando México" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: CozumelLinksAdmin,
});

function fmt(v: string | null): string {
  if (!v) return "—";
  return new Date(v).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
}

function CozumelLinksAdmin() {
  const qc = useQueryClient();
  const [customerName, setCustomerName] = useState("");
  const [label, setLabel] = useState("");
  const [copied, setCopied] = useState<string | null>(null);

  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://www.mostrandomexico.mx";

  const { data, isLoading } = useQuery({
    queryKey: ["cozumel-nl-links"],
    queryFn: () => listCozumelGuideLinks(),
  });

  const create = useMutation({
    mutationFn: () =>
      createCozumelGuideLink({
        data: {
          ...(customerName.trim() ? { customerName: customerName.trim() } : {}),
          ...(label.trim() ? { label: label.trim() } : {}),
        },
      }),
    onSuccess: () => {
      setCustomerName("");
      setLabel("");
      void qc.invalidateQueries({ queryKey: ["cozumel-nl-links"] });
    },
  });

  const revoke = useMutation({
    mutationFn: (v: { id: string; revoked: boolean }) =>
      setCozumelGuideLinkRevoked({ data: v }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["cozumel-nl-links"] }),
  });

  async function copy(url: string, id: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /* clipboard unavailable */
    }
  }

  const rows = data?.rows ?? [];

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-5xl px-4 py-8">
        <AdminNav />
        <h1 className="mt-6 flex items-center gap-2 text-2xl font-bold">
          <Sparkles className="h-5 w-5 text-primary" />
          Maya · Enlace público
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Guía digital <strong>Ontdek Cozumel</strong> (neerlandés) para el tour{" "}
          <em>Excursión a Cozumel con transporte incluido</em>. Cada enlace es único, no requiere
          login y permite <strong>una sola descarga</strong> del audio MP3.
        </p>

        {/* Generate */}
        <section className="mt-6 rounded-xl border border-border bg-card p-4">
          <h2 className="font-semibold">Generar enlace</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <Input
              placeholder="Nombre del cliente (opcional)"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
            <Input
              placeholder="Etiqueta / reserva (opcional)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <Button onClick={() => create.mutate()} disabled={create.isPending}>
              {create.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              Generar enlace
            </Button>
          </div>
          {create.isError && (
            <p className="mt-2 text-sm text-destructive">No se pudo generar el enlace.</p>
          )}
        </section>

        {/* List */}
        <div className="mt-6 space-y-4">
          {isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}
          {!isLoading && rows.length === 0 && (
            <p className="text-sm text-muted-foreground">Aún no hay enlaces generados.</p>
          )}
          {rows.map((r) => {
            const url = `${origin}/gids/cozumel/${r.token}`;
            const used = !!r.downloaded_at;
            const revoked = !!r.revoked_at;
            const waText = `Ontdek Cozumel — jouw digitale reisgids 🇲🇽\n${url}`;
            return (
              <article key={r.id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-semibold">
                    {r.customer_name || "Cliente sin nombre"}
                    {r.label ? ` · ${r.label}` : ""}
                  </h3>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span
                      className={`rounded-full px-2 py-1 font-medium ${
                        revoked
                          ? "bg-destructive/10 text-destructive"
                          : used
                            ? "bg-muted text-muted-foreground"
                            : "bg-primary/10 text-primary"
                      }`}
                    >
                      {revoked ? "Revocado" : used ? "Descarga usada" : "Descarga disponible"}
                    </span>
                    <span className="rounded-full bg-muted px-2 py-1 text-muted-foreground">
                      {r.open_count} apertura(s)
                    </span>
                  </div>
                </div>

                <code className="mt-3 block break-all rounded-lg bg-muted px-3 py-2 text-xs">
                  {url}
                </code>

                <dl className="mt-3 grid gap-x-6 gap-y-1 text-xs text-muted-foreground sm:grid-cols-3">
                  <div>
                    <dt className="inline font-medium">Creado: </dt>
                    <dd className="inline">{fmt(r.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="inline font-medium">Primera apertura: </dt>
                    <dd className="inline">{fmt(r.first_opened_at)}</dd>
                  </div>
                  <div>
                    <dt className="inline font-medium">Descargado: </dt>
                    <dd className="inline">{fmt(r.downloaded_at)}</dd>
                  </div>
                </dl>

                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => void copy(url, r.id)}>
                    {copied === r.id ? (
                      <Check className="mr-1.5 h-4 w-4" />
                    ) : (
                      <Copy className="mr-1.5 h-4 w-4" />
                    )}
                    {copied === r.id ? "Copiado" : "Copiar enlace"}
                  </Button>
                  <Button size="sm" variant="outline" asChild>
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      Abrir <ExternalLink className="ml-1.5 h-4 w-4" />
                    </a>
                  </Button>
                  <Button size="sm" variant="ghost" asChild>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(waText)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Compartir por WhatsApp
                    </a>
                  </Button>
                  <Button
                    size="sm"
                    variant={revoked ? "outline" : "destructive"}
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate({ id: r.id, revoked: !revoked })}
                  >
                    {revoked ? (
                      <>
                        <RotateCcw className="mr-1.5 h-4 w-4" /> Reactivar
                      </>
                    ) : (
                      <>
                        <Ban className="mr-1.5 h-4 w-4" /> Revocar
                      </>
                    )}
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      </main>
    </div>
  );
}
